import { SubscribeNotifyButton, Notificaties2Context, hasNoRoleIsw, useRequestInit } from "adviesbox-shared";
import { FormikContextType } from "formik";
import React, { ReactElement, useRef, useState, useCallback, useEffect, useContext, useMemo } from "react";
import { RapportageState } from "../../../rapportage2/infra/rapportage-schema";
import { sendReportRequest } from "../../../rapportage2/rapportage-api";
import { useParams } from "react-router-dom";
import { RouteParams } from "adviesbox-shared/utils/types";
import { NieuwRapport } from "../../../.generated/foundation/foundationtypes";

// Globaal register om gemonteerde instanties bij te houden
const mountedInstances = new Map<
  string,
  { setShowIframe: (value: boolean) => void; iframeUrl: React.MutableRefObject<string | null> }
>();
let instanceCounter = 0;

export type RapportViewerProps<T = any> = {
  /**
   * De RapportageStructuur ID om te gebruiken voor het genereren van het rapport
   */
  rapportStructuurId: string;

  /**
   * De naam van de RapportageState (gebruikt voor identificatie)
   */
  rapportNaam: string;

  /**
   * Formik context voor het formulier
   */
  formikContext: FormikContextType<T>;

  /**
   * Optioneel: VoorstelId om te gebruiken (indien niet opgegeven, wordt de route param gebruikt)
   */
  voorstelId?: string | null;

  /**
   * Optioneel: Knoptekst voor de genereer knop (standaard "Rapport genereren")
   */
  buttonText?: string;

  /**
   * Optioneel: Extra CSS class voor de knop container
   */
  className?: string;

  /**
   * Optioneel: Of dit een preview rapport is (standaard false)
   */
  preview?: boolean;

  /**
   * Optioneel: Aangepaste iframe hoogte (standaard "800px")
   */
  iframeHeight?: string;

  /**
   * Optioneel: Toon de dev "Rapport queue" knop (standaard true voor ISW gebruikers)
   */
  showDevButton?: boolean;
};

/**
 * RapportViewer - Een herbruikbaar component voor het genereren en weergeven van rapporten
 *
 * Dit component biedt:
 * - Een "Rapport genereren" knop die rapportgeneratie triggert
 * - Een "Rapport queue" knop voor ontwikkelaars om rapporten direct in de wachtrij te plaatsen
 * - Een iframe die het gegenereerde rapport weergeeft wanneer het klaar is
 * - Automatische notificatie afhandeling voor rapportage voltooiing
 *
 * @example
 * ```tsx
 * <RapportViewer
 *   rapportStructuurId="00000000-0000-0000-0000-000000000010"
 *   rapportNaam="MotiveringHypotheekVerantwoordeWoonlasten"
 *   formikContext={formik}
 *   preview={true}
 * />
 * ```
 */
export const RapportViewerComponent = <T extends any = any>({
  rapportStructuurId,
  rapportNaam,
  formikContext,
  voorstelId,
  buttonText = "Rapport genereren",
  className = "",
  preview = false,
  iframeHeight = "800px",
  showDevButton = true
}: RapportViewerProps<T>): ReactElement => {
  const instanceId = useRef(`rapport-viewer-${++instanceCounter}`).current;
  const iframeUrl = useRef<string | null>(null);
  const [showIframe, setShowIframe] = useState(false);
  const setShowIframeRef = useRef(setShowIframe);
  const { user, settings } = useRequestInit<{ vestiging: string; adviesdossier: string; voorstel: string }>();
  const userRef = useRef(user);
  const paramContext = useParams<RouteParams>();
  const { subscribeNotificationEvent } = useContext(Notificaties2Context);
  const isIswMedewerker = !hasNoRoleIsw(user);

  // Maak de RapportageState voor dit rapport
  const rapportageState: RapportageState = useMemo(
    () => ({
      structuurId: rapportStructuurId,
      naam: rapportNaam,
      volgnummer: 1,
      elementen: [],
      filteredElementen: [],
      selectedElement: ""
    }),
    [rapportStructuurId, rapportNaam]
  );

  // Maak de request init voor de SubscribeNotifyButton
  /* istanbul ignore next */
  const getRequestInit = useCallback(
    (correlationId: string): RequestInit => {
      const nieuwRapport: NieuwRapport = {
        adviesDossierId: paramContext.adviesdossier,
        voorstelId: voorstelId ?? paramContext.voorstel,
        rapportStructuurId: rapportStructuurId,
        gewensteRapportNaam: null,
        preview: preview
      };

      const formData = new FormData();
      formData.append("JsonValues", JSON.stringify(nieuwRapport));

      return {
        headers: {
          authorization: `${user?.token_type} ${user?.access_token}`,
          "Ocp-Apim-Subscription-Key": settings.OcpApimSubscriptionKey,
          vestigingId: paramContext.vestiging,
          correlationId: correlationId
        },
        method: "POST",
        body: formData
      };
    },
    [
      settings,
      user,
      paramContext.adviesdossier,
      paramContext.voorstel,
      paramContext.vestiging,
      voorstelId,
      rapportStructuurId,
      preview
    ]
  );

  const requestInitNieuwRapport = useCallback(
    (correlationId: string): RequestInit => {
      return getRequestInit(correlationId);
    },
    [getRequestInit]
  );

  // Update refs met de laatste waarden
  setShowIframeRef.current = setShowIframe;
  userRef.current = user;

  // Registreer/deregistreer instantie
  useEffect(() => {
    mountedInstances.set(instanceId, {
      setShowIframe: (value: boolean) => setShowIframeRef.current(value),
      iframeUrl
    });
    return () => {
      mountedInstances.delete(instanceId);
    };
  }, [instanceId]);

  // Stabiele handler die het globale register gebruikt
  const handleNotificationEvent = useCallback(async (data: any) => {
    // Construeer de iframe URL van de notificatie data
    const { rapportId, vestigingId } = data.body;
    const { adviesDossierId } = data.body.repliesTo;

    if (rapportId && vestigingId && adviesDossierId && userRef.current?.access_token) {
      // Bepaal de rapport basis URL
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" || hostname === "test.advies.adviesbox.nl"
          ? "https://test.rapport.adviesbox.nl"
          : "https://rapport.adviesbox.nl";

      // Gebruik het token van de huidige gebruiker in plaats van die van de notificatie om 401 fouten te voorkomen
      const constructedUrl = `${baseUrl}/vestiging/${vestigingId}/adviesdossier/${adviesDossierId}/rapport/${rapportId}?bearer=${userRef.current?.access_token}`;

      // Update alle gemonteerde instanties
      mountedInstances.forEach(instance => {
        instance.iframeUrl.current = constructedUrl;
        instance.setShowIframe(true);
      });
    }
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    // Abonneer op rapport notificatie events
    subscribeNotificationEvent(
      "nieuw-rapport-result",
      200,
      handleNotificationEvent,
      data => data.body.repliesTo.preview === preview
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler voor de dev "Rapport queue" knop
  const handleRapportQueue = useCallback(async () => {
    if (process.env.REACT_APP_RAPPORT_CONNECTION_STRING) {
      await sendReportRequest(
        rapportageState,
        paramContext.adviesdossier,
        voorstelId ?? paramContext.voorstel,
        user,
        paramContext.vestiging,
        preview
      );
    }
  }, [rapportageState, paramContext, voorstelId, user, preview]);

  return (
    <>
      <div className={`d-flex justify-content-center ${className}`}>
        <SubscribeNotifyButton
          className="mt-4"
          context={formikContext}
          icon={null}
          name={`rapport-${rapportNaam}`}
          buttonText={buttonText}
          createCommandRequest={requestInitNieuwRapport}
          url={`${settings.foundationOrigin}/FrontBus/nieuw-rapport`}
          alwaysShow={true}
          showSpinner={true}
          isSubmitButton={false}
        />

        {showDevButton && isIswMedewerker && (
          <button type="button" className="btn btn-secondary mt-4 ml-2" onClick={handleRapportQueue}>
            Rapport queue (dev&apos;s)
          </button>
        )}
      </div>

      {/* Iframe voor rapport weergave */}
      {showIframe && iframeUrl.current && (
        <div className="mt-4">
          <iframe
            key={iframeUrl.current}
            src={iframeUrl.current}
            width="100%"
            height={iframeHeight}
            style={{ border: "1px solid #ccc", borderRadius: "4px" }}
            title={`Rapport: ${rapportNaam}`}
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      )}
    </>
  );
};

export const RapportViewer = RapportViewerComponent;
